import type React from "react";
import type { BacktestCostScenario, BacktestReport, BacktestStats, BacktestStep, BacktestYear, Num } from "../types";

/**
 * Аудит 5: «Результаты по годам», графики по годам, «до / после исправлений», чувствительность к издержкам — для
 * канона Уильямса (единственные правила сделок).
 * Все показатели — после издержек, если не сказано иное. Неполный год помечен; год без данных — N/A с причиной.
 */

const CANON_COLOR = "var(--p-canon)";

const pct = (v: Num, digits = 2) => (v == null ? "N/A" : `${v > 0 ? "+" : ""}${v.toFixed(digits).replace(".", ",")}%`);
const plain = (v: Num, digits = 2) => (v == null ? "N/A" : v.toFixed(digits).replace(".", ","));
const rub = (v: Num) => (v == null ? "N/A" : `${v > 0 ? "+" : ""}${Math.round(v).toLocaleString("ru-RU")} ₽`);
const tone = (v: Num) => (v == null || v === 0 ? "" : v > 0 ? "up" : "down");
const pfText = (s: BacktestStats) =>
  s.trades === 0 ? "N/A" : s.profitFactor != null ? plain(s.profitFactor) : s.pfInfinite ? `∞ (${s.trades} сд., без убытков)` : "N/A";
const yearLabel = (y: BacktestYear) => `${y.year}${y.partial ? "*" : ""}`;

export function ConventionsNote({ data }: { data: BacktestReport }) {
  return (
    <details className="pane-help bt-howto">
      <summary>Исполнение, издержки и правила подсчёта</summary>
      <div className="pane-help-body">
        <p>
          <b>Исполнение:</b> {data.execution}.
        </p>
        <ul>
          {data.conventions.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        {data.rules.length > 0 && (
          <>
            <p>
              <b>Правила и их источник:</b>
            </p>
            <ul>
              {data.rules.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    </details>
  );
}

export function Limitations({ items }: { items: string[] }) {
  if (!items.length) return null;
  return (
    <div className="notice notice-warn bt-warnings" role="note">
      <b>Что нельзя считать надёжным</b>
      <ul>
        {items.map((w) => (
          <li key={w}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

/** Полная статистика канона по годам: строки — показатели, столбцы — годы. */
export function YearsTable({ years, title }: { years: BacktestYear[]; title?: string }) {
  if (!years.length) return null;
  const rows: [string, (y: BacktestYear) => React.ReactNode, string?][] = [
    ["Сделок (открыты в году, закрыты)", (y) => String(y.realized.trades)],
    ["Прибыльных / убыточных / нулевых", (y) => `${y.realized.wins} / ${y.realized.losses} / ${y.realized.zeros}`],
    ["Win Rate", (y) => (y.realized.winRate == null ? "N/A" : `${plain(y.realized.winRate, 1)}%`), "прибыльные / все закрытые"],
    ["Profit Factor", (y) => pfText(y.realized)],
    ["Средняя сделка, %", (y) => pct(y.realized.avgPct)],
    ["Средняя сделка, ₽ (1 контракт)", (y) => (y.realized.rubTrades ? `${rub(y.realized.avgRub)} · ${y.realized.rubTrades} сд.` : "N/A"), "только рынки с известной стоимостью пункта"],
    ["Медианная сделка", (y) => pct(y.realized.medianPct)],
    ["Средняя прибыльная", (y) => pct(y.realized.avgWinPct)],
    ["Средняя убыточная", (y) => pct(y.realized.avgLossPct)],
    ["Expectancy (после издержек)", (y) => pct(y.realized.expectancyPct)],
    ["Сумма доходностей сделок", (y) => pct(y.realized.sumPct), "сумма % закрытых сделок года — не доходность портфеля"],
    ["Сумма до издержек", (y) => (y.realized.trades ? pct(y.realized.sumPctGross) : "N/A")],
    ["Сумма, ₽ (1 контракт)", (y) => (y.realized.rubTrades ? rub(y.realized.rubTotal) : "N/A")],
    ["Просадка (сделки года по выходу)", (y) => (y.cohortDrawdownPct == null ? "N/A" : `−${plain(y.cohortDrawdownPct)} п.`), "в пунктах суммы %"],
    ["Средняя длительность, дней", (y) => (y.realized.avgDays == null ? "N/A" : plain(y.realized.avgDays, 1))],
    ["Инструментов", (y) => String(y.realized.instruments)],
    ["Инструментов в плюсе / минусе", (y) => `${y.realized.instrumentsPositive} / ${y.realized.instrumentsNegative}`],
    ["Доля 5 лучших сделок", (y) => (y.realized.top5TradesShare == null ? "N/A" : `${plain(y.realized.top5TradesShare, 0)}%`), "от суммы года; > 100% — без них год в минусе"],
    ["Доля 5 лучших инструментов", (y) => (y.realized.top5MarketsShare == null ? "N/A" : `${plain(y.realized.top5MarketsShare, 0)}%`)],
    ["Открыты в году, не закрыты на 31.12", (y) => (y.openAtYearEnd ? `${y.openAtYearEnd} · ${pct(y.openUnrealizedPct)} нереализ.` : "0")],
    ["Сделки прошлых лет, закрытые в году", (y) => (y.carriedIn ? `${y.carriedIn} · ${pct(y.carriedInPct)}` : "0")],
    ["Календарный результат (mark-to-market)", (y) => pct(y.calendarPct), "изменение суммы % по всем сделкам за календарный год"],
    ["Календарный результат, ₽", (y) => rub(y.calendarRub)],
    ["Календарная просадка", (y) => (y.calendarDrawdownPct == null ? "N/A" : `−${plain(y.calendarDrawdownPct)} п.`), "по недельной кривой суммы %"],
  ];
  return (
    <div className="table-scroll">
      {title && <h3>{title}</h3>}
      <table className="data-table bt-table bt-years">
        <thead>
          <tr>
            <th className="text">Показатель</th>
            {years.map((y) => (
              <th key={y.year} className={y.partial ? "is-partial" : ""}>
                {y.year}
                {y.partial && <small> неполный, по {y.to}</small>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {years.some((y) => y.naReason) && (
            <tr>
              <td className="text muted">Почему N/A</td>
              {years.map((y) => (
                <td key={y.year} className="text muted bt-na">
                  {y.naReason ?? ""}
                </td>
              ))}
            </tr>
          )}
          {rows.map(([label, f, note]) => (
            <tr key={label}>
              <td className="text">
                {label}
                {note && <small className="muted"> · {note}</small>}
              </td>
              {years.map((y) => (
                <td key={y.year} className={y.partial ? "is-partial" : ""}>
                  {y.naReason && y.realized.trades === 0 && !y.calendarPct ? "N/A" : f(y)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type Metric = "trades" | "winRate" | "profitFactor" | "avgPct" | "sumPct" | "drawdown";
const METRIC: Record<Metric, { label: string; get: (y: BacktestYear) => Num; fmt: (v: Num) => string; base?: number }> = {
  trades: { label: "Количество сделок", get: (y) => (y.naReason && !y.realized.trades ? null : y.realized.trades), fmt: (v) => (v == null ? "N/A" : String(v)) },
  winRate: { label: "Win Rate, %", get: (y) => y.realized.winRate, fmt: (v) => (v == null ? "N/A" : `${plain(v, 1)}%`) },
  profitFactor: { label: "Profit Factor", get: (y) => y.realized.profitFactor, fmt: (v) => plain(v), base: 1 },
  avgPct: { label: "Средняя сделка, %", get: (y) => y.realized.avgPct, fmt: (v) => pct(v), base: 0 },
  sumPct: { label: "Результат после издержек (сумма % сделок)", get: (y) => (y.realized.trades ? y.realized.sumPct : null), fmt: (v) => pct(v, 1), base: 0 },
  drawdown: { label: "Максимальная просадка (сделки года), п.", get: (y) => y.cohortDrawdownPct, fmt: (v) => (v == null ? "N/A" : `−${plain(v, 1)}`) },
};

/** Графики по годам для канона: столбец на год, неполный год заштрихован. */
export function YearCharts({ years }: { years: BacktestYear[] }) {
  if (!years.length) return null;
  return (
    <section>
      <h2>Графики по годам</h2>
      <div className="bt-charts">
        {(["profitFactor", "winRate", "sumPct", "drawdown", "trades"] as Metric[]).map((m) => (
          <YearBars key={m} metric={m} years={years} />
        ))}
      </div>
      <p className="muted bt-legend">
        <span>Год сделки — год открытия, после издержек. Штриховка и * — неполный год. N/A — нет данных (не ноль).</span>
      </p>
    </section>
  );
}

function YearBars({ metric, years }: { metric: Metric; years: BacktestYear[] }) {
  const m = METRIC[metric];
  const vals = years.map((y) => m.get(y));
  const flat = vals.filter((x): x is number => x != null);
  const base = m.base ?? 0;
  const lo = Math.min(base, ...flat, 0);
  const hi = Math.max(base, ...flat, metric === "profitFactor" ? 2 : 1);
  const W = 360, H = 170, padL = 22, padB = 22, padT = 16;
  const plotH = H - padB - padT;
  const y = (v: number) => padT + (hi - v) / (hi - lo || 1) * plotH;
  const gw = (W - padL - 6) / years.length;
  const bw = Math.max(4, Math.min(48, gw - 14));
  return (
    <figure className="bt-chart">
      <figcaption>{m.label}</figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`${m.label} по годам`}>
        <defs>
          <pattern id={`hatch-${metric}`} patternUnits="userSpaceOnUse" width="5" height="5" patternTransform="rotate(45)">
            <rect width="5" height="5" fill="var(--surface)" opacity="0" />
            <line x1="0" y1="0" x2="0" y2="5" stroke="var(--surface)" strokeWidth="2" opacity="0.55" />
          </pattern>
        </defs>
        <line x1={padL} x2={W - 4} y1={y(base)} y2={y(base)} className="bt-axis" />
        <text x={padL - 4} y={y(base) + 4} className="bt-tick" textAnchor="end">{base}</text>
        {years.map((yr, yi) => {
          const cx = padL + yi * gw + gw / 2;
          const val = vals[yi];
          const x = cx - bw / 2;
          const top = val == null ? 0 : Math.min(y(val), y(base));
          const h = val == null ? 0 : Math.max(1, Math.abs(y(val) - y(base)));
          return (
            <g key={yr.year}>
              {val == null ? (
                <text x={cx} y={y(base) - 8} className="bt-na-tick" textAnchor="middle">
                  N/A
                </text>
              ) : (
                <>
                  <rect x={x} y={top} width={bw} height={h} fill={CANON_COLOR} opacity={yr.partial ? 0.65 : 1}>
                    <title>{`${yr.year}${yr.partial ? " (неполный)" : ""}: ${m.fmt(val)}`}</title>
                  </rect>
                  {yr.partial && <rect x={x} y={top} width={bw} height={h} fill={`url(#hatch-${metric})`} />}
                  <text x={cx} y={(val >= base ? top : top + h) + (val >= base ? -3 : 11)} className="bt-tick" textAnchor="middle">
                    {m.fmt(val).replace("+", "")}
                  </text>
                </>
              )}
              <text x={cx} y={H - 6} className="bt-tick" textAnchor="middle">
                {yr.year}
                {yr.partial ? "*" : ""}
              </text>
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

/** «До / после исправлений»: шаги меняют по одному фактору. */
export function StepsSection({ steps }: { steps: BacktestStep[] }) {
  if (!steps.length) return null;
  const yearsOf = steps[0].years.filter((y) => !(y.naReason && y.realized.trades === 0));
  return (
    <section>
      <h2>До и после исправлений</h2>
      <p className="lede">
        Каждая строка меняет ровно один фактор относительно предыдущей, поэтому видно, какое исправление как повлияло на
        результат. «Исходный расчёт» — канон редакции аудита 4 в прежних условиях; он воспроизводит прежние цифры сделка в
        сделку. Параметры канона не подбирались.
      </p>
      {[steps].map((rows) => (
        <div key="canon" className="table-scroll">
          <table className="data-table bt-table">
            <thead>
              <tr>
                <th className="text">Шаг</th>
                <th>Сделок</th>
                <th>Win Rate</th>
                <th>Profit Factor</th>
                <th>Средняя сделка</th>
                <th title="Сумма % всех закрытых сделок">Сумма %</th>
                <th title="Сумма % в порядке выхода, все рынки вместе">Просадка, п.</th>
                {yearsOf.map((y) => (
                  <th key={y.year}>PF {yearLabel(y)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const s = r.total.stats;
                return (
                  <tr key={r.key}>
                    <td className="text wrap">
                      <b>{r.title}</b>
                      <small className="muted"> · {r.change}</small>
                    </td>
                    <td>{s.trades}</td>
                    <td>{s.winRate == null ? "N/A" : `${plain(s.winRate, 1)}%`}</td>
                    <td className={tone(s.profitFactor == null ? null : s.profitFactor - 1)}>
                      <b>{pfText(s)}</b>
                    </td>
                    <td className={tone(s.avgPct)}>{pct(s.avgPct)}</td>
                    <td className={tone(s.sumPct)}>{pct(s.sumPct, 1)}</td>
                    <td>−{plain(s.maxDrawdownPct, 1)}</td>
                    {yearsOf.map((y0) => {
                      const y = r.years.find((x) => x.year === y0.year);
                      return <td key={y0.year}>{y ? pfText(y.realized) : "N/A"}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </section>
  );
}

export function CostSection({ costs, current }: { costs: BacktestCostScenario[]; current: number }) {
  if (!costs.length) return null;
  const years = costs[0].years.filter((y) => !(y.naReason && y.realized.trades === 0));
  return (
    <section>
      <h2>Чувствительность к издержкам</h2>
      <p className="lede">
        Точный тариф биржи и брокера и проскальзывание в расчёт не заложены — это допущение. Здесь те же сделки канона
        с разными издержками туда-обратно (% цены контракта). Основная таблица — {plain(current)} %.
      </p>
      <div className="table-scroll">
        <table className="data-table bt-table">
          <thead>
            <tr>
              <th>Издержки</th>
              <th>Win Rate</th>
              <th>Profit Factor</th>
              <th>Expectancy</th>
              <th>Сумма %</th>
              {years.map((y) => (
                <th key={y.year}>PF {yearLabel(y)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {costs.map((c) => (
              <tr key={c.costPct} className={Math.abs(c.costPct - current) < 1e-9 ? "bt-current" : ""}>
                <td>{plain(c.costPct)} %</td>
                <td>{c.stats.winRate == null ? "N/A" : `${plain(c.stats.winRate, 1)}%`}</td>
                <td className={tone(c.stats.profitFactor == null ? null : c.stats.profitFactor - 1)}>{pfText(c.stats)}</td>
                <td className={tone(c.stats.expectancyPct)}>{pct(c.stats.expectancyPct)}</td>
                <td className={tone(c.stats.sumPct)}>{pct(c.stats.sumPct, 1)}</td>
                {years.map((y0) => {
                  const y = c.years.find((x) => x.year === y0.year);
                  return <td key={y0.year}>{y ? pfText(y.realized) : "N/A"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
